import i18next from 'i18next';

import { IntegrationServices } from '@/modules/common/types/integrations';

import { IntegrationConnectionStatus } from '../components/resolve-duplicates-modal/types';
import { BaseOnboardingEntity } from '../types/base-onboarding-entity';

/**
 * Finds field values that are identical across all items in the array.
 * Returns null for fields that differ between items.
 *
 * @param equalityFn - Optional custom equality check for nested values (e.g. comparing by `.id`)
 */
export function getCommonValues<T extends Record<string, any>>(
  arr: T[],
  equalityFn?: (a: any, b: any) => boolean
): Partial<Record<keyof T, any>> {
  if (arr.length === 0) return {};

  const defaultEquality = (a: any, b: any) => a === b;
  const isEqual = equalityFn ?? defaultEquality;

  const result: Partial<Record<keyof T, any>> = {};
  const keys = Object.keys(arr[0]) as (keyof T)[];

  for (const key of keys) {
    const firstValue = arr[0][key];
    const isAllEqual = arr.every(item => isEqual(item[key], firstValue));
    result[key] = isAllEqual ? firstValue : null;
  }

  return result;
}

/**
 * Deduplicates an array by a given key field.
 *
 * @param getId - Optional function to extract a comparable ID from the value
 *                (e.g. for nested objects like `{ id: 1, name: 'foo' }`)
 */
export function removeDuplicatesByKey<T extends Record<string, any>>(
  arr: T[],
  key: keyof T,
  getId?: (value: any) => any
): T[] {
  const seen = new Set<any>();
  return arr.filter(item => {
    const value = item[key];

    if (!value && getId) {
      return false;
    }

    const id = getId ? getId(value) : value;
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
}

/**
 * Adds "Person 1", "Person 2", etc. labels to entities for the resolve-onboarding-conflicts modal.
 */
export function getEntitiesWithPersonLabel<T extends BaseOnboardingEntity>(
  items: T[],
  i18nKey: string
): (T & { label: string })[] {
  return items.map((item, index) => ({
    ...item,
    label: i18next.t(i18nKey, { index: index + 1 }),
  }));
}

interface IntegrationDataResult {
  options: Record<string, { label: string; value: string; id: string | number }[]>;
  defaultValues: Record<string, string>;
  cells: Record<string, { status: IntegrationConnectionStatus }[]>;
}

/**
 * Builds integration selection data for the resolve-duplicates modal.
 * For each integration, determines:
 * - Which items have it connected (options for the dropdown)
 * - A sensible default value
 * - Cell statuses for each item (connected/notFound)
 */
export function getIntegrationData<T extends BaseOnboardingEntity & { label: string }>(
  selectedItems: T[],
  integrationsList: IntegrationServices[],
  i18nNoneLabel: string
): IntegrationDataResult {
  const noneOption = {
    label: i18nNoneLabel,
    value: 'none',
    id: 'none' as string | number,
  };

  return integrationsList.reduce<IntegrationDataResult>(
    (acc, integration) => {
      const options = selectedItems
        .filter(item => item.connectedIntegrations.includes(integration))
        .map(item => ({ label: item.label, value: item.id.toString(), id: item.id }));

      const defaultValue =
        options.length === 0 ? 'none' : options.length === 1 ? options[0].value : '';

      const cells = selectedItems.map(item => ({
        status: item.connectedIntegrations.includes(integration)
          ? IntegrationConnectionStatus.Connected
          : IntegrationConnectionStatus.NotFound,
      }));

      return {
        options: { ...acc.options, [integration]: [...options, noneOption] },
        defaultValues: { ...acc.defaultValues, [integration]: defaultValue },
        cells: { ...acc.cells, [integration]: cells },
      };
    },
    { options: {}, defaultValues: {}, cells: {} }
  );
}
